import { useState } from "react";
import { ChevronDown, Phone } from "lucide-react";

/*
 * AreasFAQ — Section 7/8 for /service-areas/
 * Navy section — 10 location-focused FAQs with accordion + FAQPage JSON-LD.
 * Paste this ENTIRE file into src/pages/ServiceAreas/sections/AreasFAQ.tsx
 * replacing the placeholder content.
 */

const GOLD = "#F5A623";
const NAVY = "#081438";
const NAVY2 = "#0f2347";
const FAQS = [{
  q: "Which suburbs in Sydney do you service?",
  a: "We cover 217+ suburbs across Greater Sydney — from the Eastern Suburbs and North Shore to Western Sydney, the Hills District, Sutherland Shire, Canterbury-Bankstown, Inner West, Northern Beaches, South Sydney and beyond. We also service Wollongong, the Central Coast and Blue Mountains on scheduled days."
}, {
  q: "Do you charge extra for distant suburbs like Wollongong or the Central Coast?",
  a: "We run dedicated service days for Wollongong, Central Coast and Blue Mountains to keep travel costs down. Call 0485 000 933 and we'll let you know the next available day for your area — there's no hidden call-out premium."
}, {
  q: "How fast can you get to my suburb?",
  a: "For most Sydney metro suburbs, we offer same-day service when booked before noon. Our base in Bass Hill puts us within 30–60 minutes of most areas during business hours. Outer regions like the Central Coast and Blue Mountains are serviced on scheduled days."
}, {
  q: "Can I book a specific time for my area?",
  a: "Yes. We offer morning (7am–12pm) and afternoon (12pm–5pm) booking windows across all service areas. For emergency jobs, we'll give you an ETA within 15 minutes of your call."
}, {
  q: "Do you service both residential and commercial properties?",
  a: "Absolutely. We handle everything from single residential garage doors to multi-bay commercial roller shutters across all our service areas. Same warranty, same quality."
}, {
  q: "What garage door brands do you service in my area?",
  a: "We service all major Australian and international brands including B&D, Merlin, Chamberlain, ATA, Steel-Line, Gliderol, Centurion, LiftMaster, Marantec, Grifco and Hormann. We carry parts for all of them on our van."
}, {
  q: "Is the 1-Year Labour Warranty valid across all suburbs?",
  a: "Yes — our 1-Year Labour Warranty applies to every job in every suburb we service, from Bondi to the Blue Mountains. No exceptions."
}, {
  q: "What if my suburb isn't listed on your website?",
  a: "If you're within Greater Sydney, Wollongong, the Central Coast or the Blue Mountains, we almost certainly cover you. Call 0485 000 933 or send a WhatsApp and we'll confirm on the spot."
}, {
  q: "Do you offer emergency service to all areas?",
  a: "We offer priority emergency response across all Sydney metro suburbs. For outer regions (Wollongong, Central Coast, Blue Mountains), emergency availability depends on the day — call us and we'll find the fastest solution."
}, {
  q: "How do I get a quote for my area?",
  a: "Call 0485 000 933, send a WhatsApp to the same number, or fill out the quote form on this page. Tell us your suburb, your door type and the issue — we'll give you a fixed-price quote, usually within the hour."
}];
const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a
    }
  }))
};
export default function AreasFAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return <section id="areas-faq" style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from AreasReviews bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill="#FAF7F1" />
        </svg>
      </div>

      {/* JSON-LD FAQPage schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(FAQ_SCHEMA)
    }} />

      {/* Grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="afq-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#afq-grain)" />
      </svg>

      {/* Scanlines */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />

      {/* GG watermark */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "280px",
      fontWeight: 900,
      color: `${GOLD}08`,
      fontFamily: "Plus Jakarta Sans,sans-serif",
      pointerEvents: "none",
      zIndex: 1,
      userSelect: "none",
      letterSpacing: "-0.05em"
    }}>
        FAQ
      </div>

      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Two-column: header left, accordion right */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "48px"
      }} className="lg:!grid-cols-[1fr_2fr]">

          {/* Left — sticky header */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px"
          }}>
              <div style={{
              width: "32px",
              height: "2px",
              background: GOLD
            }} />
              <span style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: `${GOLD}BB`
            }}>
                Common Questions
              </span>
            </div>
            <h2 style={{
            fontFamily: "Plus Jakarta Sans,sans-serif",
            fontWeight: 800,
            fontSize: "clamp(26px,3.5vw,42px)",
            lineHeight: 1.1,
            color: "#FFFFFF",
            marginBottom: "16px"
          }}>
              Service Area<br /><span style={{
              color: GOLD
            }}>FAQs</span>
            </h2>
            <p style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "clamp(14px,1.5vw,16px)",
            color: "rgba(255,255,255,0.6)",
            lineHeight: 1.7,
            marginBottom: "28px"
          }}>
              Everything you need to know about our coverage, response times and booking across Sydney.
            </p>
            <a href="tel:0485000933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: GOLD,
            color: NAVY,
            padding: "13px 24px",
            borderRadius: "8px",
            fontFamily: "Manrope,sans-serif",
            fontWeight: 700,
            fontSize: "14px",
            textDecoration: "none"
          }}>
              {/** @preserve lucide-do-not-replace */}
              <span data-icon-container="lucide-do-not-replace"><Phone size={16} color={NAVY} strokeWidth={2.5} /></span>
              Still have questions? Call us
            </a>
          </div>

          {/* Right — accordion */}
          <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px"
        }}>
            {FAQS.map((faq, i) => {
            const isOpen = open === i;
            return <div key={i} style={{
              background: isOpen ? NAVY2 : "rgba(255,255,255,0.03)",
              border: `1px solid ${isOpen ? `${GOLD}40` : "rgba(255,255,255,0.08)"}`,
              borderRadius: "10px",
              overflow: "hidden",
              transition: "border-color 0.2s"
            }}>
                  <button onClick={() => setOpen(isOpen ? null : i)} style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "18px 20px",
                background: "none",
                border: "none",
                cursor: "pointer",
                textAlign: "left"
              }}>
                    <span style={{
                  fontFamily: "Plus Jakarta Sans,sans-serif",
                  fontWeight: 600,
                  fontSize: "15px",
                  color: isOpen ? GOLD : "#FFFFFF",
                  lineHeight: 1.3,
                  flex: 1
                }}>
                      {faq.q}
                    </span>
                    {/** @preserve lucide-do-not-replace */}
                    <span data-icon-container="lucide-do-not-replace" style={{
                  flexShrink: 0,
                  transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s"
                }}>
                      <ChevronDown size={18} color={isOpen ? GOLD : "rgba(255,255,255,0.4)"} strokeWidth={2} />
                    </span>
                  </button>
                  {isOpen && <div style={{
                padding: "0 20px 18px",
                fontFamily: "Manrope,sans-serif",
                fontSize: "14px",
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.75
              }}>
                      {faq.a}
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Wave bottom — DUPLICATE in AreasCTA top */}
      <div style={{
      position: "relative",
      zIndex: 3,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#FAF7F1" />
        </svg>
      </div>
    </section>;
}